import React, {
    useState,
    useEffect,
    useRef,
    useImperativeHandle,
    forwardRef,
} from "react";
import { ScrollArea } from "@shadcn-in-nx/ui/scroll-area";
import { Input } from "@shadcn-in-nx/ui/input";
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuTrigger,
} from "@shadcn-in-nx/ui/context-menu";
import {
    ChevronDown,
    ChevronRight,
    File,
    Folder,
    FolderOpen,
    Search,
    FileCode,
    ChevronLeft,
    X,
    FolderPlus,
    Pencil,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useEditor } from "../EditorContext";
import InlineFileInput from "./InlineFileInput";

const CodeSection = forwardRef(
    (
        {
            components = [],
            onFileSelect,
            selectedFile,
            openFiles = [],
            onCloseFile,
            onFileOpen,
            showFileSearch,
            onFileSearchClose,
            dashboardSection,
            onBackToMenu,
            onCloseSidebar,
            onCreateFile,
            onCreateFolder,
            onDeleteFile,
            onRenameFile,
            ensureCode,
        },
        ref
    ) => {
        const [activeTab, setActiveTab] = useState("files"); // "files" or "search"
        const [searchQuery, setSearchQuery] = useState("");
        const [expandedFolders, setExpandedFolders] = useState(new Set());
        const [searchResults, setSearchResults] = useState([]);
        const [fileTree, setFileTree] = useState([]);
        const [filteredFileTree, setFilteredFileTree] = useState([]);
        const [selectedIndex, setSelectedIndex] = useState(0);
        const [flatFileList, setFlatFileList] = useState([]);
        const [renamingFileId, setRenamingFileId] = useState(null);
        const [renameValue, setRenameValue] = useState("");
        const [creatingIn, setCreatingIn] = useState(null); // { path: string|null, type: 'file'|'folder' }
        const renameInputRef = useRef(null);
        const searchInputRef = useRef(null);
        const { t } = useTranslation();
        const { fileStates, prefetchedCode } = useEditor();

        // Expose focusSearch method to parent
        useImperativeHandle(ref, () => ({
            focusSearch: () => {
                setActiveTab("files");
                setTimeout(() => {
                    searchInputRef.current?.focus();
                }, 0);
            },
        }));

        // Build file tree structure from components
        useEffect(() => {
            const buildFileTree = (components) => {
                const tree = {};

                components.forEach((component) => {
                    const path = component.filePath || component.name;
                    const parts = path.split("/");
                    let current = tree;

                    parts.forEach((part, index) => {
                        if (index === parts.length - 1) {
                            // This is a file
                            current[part] = {
                                type: "file",
                                component: component,
                                path: path,
                            };
                        } else {
                            // This is a folder
                            if (!current[part]) {
                                current[part] = {
                                    type: "folder",
                                    children: {},
                                    path: parts.slice(0, index + 1).join("/"),
                                };
                            }
                            current = current[part].children;
                        }
                    });
                });

                return tree;
            };

            const sortTree = (tree) => {
                const sorted = {};
                const entries = Object.entries(tree).sort(([aName, aItem], [bName, bItem]) => {
                    if (aItem.type === "folder" && bItem.type !== "folder") return -1;
                    if (aItem.type !== "folder" && bItem.type === "folder") return 1;
                    return aName.localeCompare(bName);
                });
                entries.forEach(([name, item]) => {
                    if (item.type === "folder") {
                        sorted[name] = { ...item, children: sortTree(item.children) };
                    } else {
                        sorted[name] = item;
                    }
                });
                return sorted;
            };

            const tree = sortTree(buildFileTree(components));
            setFileTree(tree);
            setFilteredFileTree(tree);
        }, [components]);

        // Filter file tree based on search query (for Files tab)
        useEffect(() => {
            if (activeTab === "files" && searchQuery.trim()) {
                const filterTree = (tree, query) => {
                    const filtered = {};

                    Object.entries(tree).forEach(([name, item]) => {
                        if (item.type === "folder") {
                            const filteredChildren = filterTree(
                                item.children,
                                query
                            );
                            if (Object.keys(filteredChildren).length > 0) {
                                filtered[name] = {
                                    ...item,
                                    children: filteredChildren,
                                };
                                // Auto-expand folders that contain matching files
                                setExpandedFolders(
                                    (prev) => new Set([...prev, item.path])
                                );
                            }
                        } else if (item.type === "file") {
                            const fileName = name.toLowerCase();
                            const componentName =
                                item.component.name.toLowerCase();
                            const filePath = item.path.toLowerCase();

                            if (
                                fileName.includes(query.toLowerCase()) ||
                                componentName.includes(query.toLowerCase()) ||
                                filePath.includes(query.toLowerCase())
                            ) {
                                filtered[name] = item;

                                // Auto-expand parent folders of matching files
                                const pathParts = item.path.split("/");
                                for (let i = 1; i < pathParts.length; i++) {
                                    const parentPath = pathParts
                                        .slice(0, i)
                                        .join("/");
                                    setExpandedFolders(
                                        (prev) => new Set([...prev, parentPath])
                                    );
                                }
                            }
                        }
                    });

                    return filtered;
                };

                const filtered = filterTree(fileTree, searchQuery);
                setFilteredFileTree(filtered);
                setSelectedIndex(0); // Reset selection when search changes
            } else {
                setFilteredFileTree(fileTree);
                setSelectedIndex(0);
            }
        }, [searchQuery, fileTree, activeTab]);

        // Flatten file tree for keyboard navigation
        useEffect(() => {
            const flattenTree = (tree) => {
                const files = [];

                const traverse = (node) => {
                    Object.entries(node).forEach(([name, item]) => {
                        if (item.type === "file") {
                            files.push(item.component);
                        } else if (item.type === "folder" && item.children) {
                            traverse(item.children);
                        }
                    });
                };

                traverse(tree);
                return files;
            };

            const files = flattenTree(filteredFileTree);
            setFlatFileList(files);
        }, [filteredFileTree]);

        // Prefetch all component codes when switching to Search tab
        useEffect(() => {
            if (activeTab === "search" && ensureCode && components.length > 0) {
                components.forEach((component) => ensureCode(component));
            }
        }, [activeTab, components, ensureCode]);

        // Search functionality (only for Search tab)
        useEffect(() => {
            if (activeTab === "search" && searchQuery.trim()) {
                const results = [];

                const searchInComponent = (component, query) => {
                    const matches = [];
                    const code = fileStates[component.id]?.code || prefetchedCode[component.id] || component.code || "";
                    if (!code) return matches;
                    const lines = code.split("\n");

                    lines.forEach((line, index) => {
                        if (line.toLowerCase().includes(query.toLowerCase())) {
                            matches.push({
                                lineNumber: index + 1,
                                line: line.trim(),
                                component: component,
                            });
                        }
                    });

                    return matches;
                };

                components.forEach((component) => {
                    const matches = searchInComponent(component, searchQuery);
                    if (matches.length > 0) {
                        results.push({
                            component: component,
                            matches: matches,
                            totalMatches: matches.length,
                        });
                    }
                });

                setSearchResults(results);
            } else {
                setSearchResults([]);
            }
        }, [searchQuery, components, activeTab, fileStates, prefetchedCode]);

        const toggleFolder = (folderPath) => {
            setExpandedFolders((prev) => {
                const newSet = new Set(prev);
                if (newSet.has(folderPath)) {
                    newSet.delete(folderPath);
                } else {
                    newSet.add(folderPath);
                }
                return newSet;
            });
        };

        const handleFileClick = (component) => {
            if (onFileSelect) {
                onFileSelect(component);
            }
            if (onFileOpen) {
                onFileOpen(component);
            }
        };

        const handleSearchResultClick = (component, lineNumber) => {
            if (onFileSelect) {
                onFileSelect(component, lineNumber);
            }
            if (onFileOpen) {
                onFileOpen(component);
            }
        };

        const handleRenameClick = (component) => {
            const filePath = component.filePath || component.name;
            const fullFileName = filePath.split("/").pop();

            setRenamingFileId(component.id);
            setRenameValue(fullFileName);

            // Focus and select only name part (before extension) like VS Code
            setTimeout(() => {
                if (renameInputRef.current) {
                    renameInputRef.current.focus();
                    const lastDot = fullFileName.lastIndexOf(".");
                    if (lastDot > 0) {
                        renameInputRef.current.setSelectionRange(0, lastDot);
                    } else {
                        renameInputRef.current.select();
                    }
                }
            }, 0);
        };

        const handleRenameConfirm = (component, newName, originalName) => {
            if (!newName.trim()) {
                setRenamingFileId(null);
                setRenameValue("");
                return;
            }

            if (newName.trim() === originalName) {
                setRenamingFileId(null);
                setRenameValue("");
                return;
            }

            const oldPath = component.filePath || component.name;
            const newFileName = newName.trim();

            // Build new path (preserve folder structure)
            const lastSlash = oldPath.lastIndexOf("/");
            const folderPath = lastSlash > 0 ? oldPath.substring(0, lastSlash) : "";
            const newPath = folderPath
                ? `${folderPath}/${newFileName}`
                : newFileName;

            if (onRenameFile) {
                onRenameFile(oldPath, component.id, component, newPath);
            }

            setRenamingFileId(null);
            setRenameValue("");
        };

        const handleRenameCancel = () => {
            setRenamingFileId(null);
            setRenameValue("");
        };

        const handleRenameKeyDown = (e, component) => {
            if (e.key === "Enter") {
                e.preventDefault();
                const filePath = component.filePath || component.name;
                const originalFileName = filePath.split("/").pop();
                handleRenameConfirm(component, renameValue, originalFileName);
            } else if (e.key === "Escape") {
                e.preventDefault();
                handleRenameCancel();
            }
        };

        const handleTabChange = (tab) => {
            setActiveTab(tab);
            setSearchQuery(""); // Clear search when switching tabs
        };

        // Handle keyboard navigation in file list
        const handleSearchKeyDown = (e) => {
            if (activeTab !== "files" || flatFileList.length === 0) return;

            if (e.key === "ArrowDown") {
                e.preventDefault();
                setSelectedIndex((prev) =>
                    prev < flatFileList.length - 1 ? prev + 1 : prev
                );
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
            } else if (e.key === "Enter") {
                e.preventDefault();
                const selectedFile = flatFileList[selectedIndex];
                if (selectedFile) {
                    handleFileClick(selectedFile);
                    setSearchQuery(""); // Clear search after selecting
                }
            } else if (e.key === "Escape") {
                e.preventDefault();
                setSearchQuery("");
                searchInputRef.current?.blur();
            }
        };

        const renderFileTree = (tree, level = 0) => {
            return Object.entries(tree).map(([name, item]) => {
                if (item.type === "folder") {
                    const isExpanded = expandedFolders.has(item.path);
                    return (
                        <ContextMenu key={item.path}>
                            <ContextMenuTrigger asChild>
                                <div
                                    className="flex items-center py-1 px-2 hover:bg-gray-100 cursor-pointer"
                                    style={{
                                        paddingLeft: `${level * 12 + 8}px`,
                                    }}
                                    onClick={() => toggleFolder(item.path)}
                                >
                                    {isExpanded ? (
                                        <ChevronDown className="w-4 h-4 text-gray-500 mr-1" />
                                    ) : (
                                        <ChevronRight className="w-4 h-4 text-gray-500 mr-1" />
                                    )}
                                    {isExpanded ? (
                                        <FolderOpen className="w-4 h-4 text-blue-500 mr-2" />
                                    ) : (
                                        <Folder className="w-4 h-4 text-blue-500 mr-2" />
                                    )}
                                    <span className="text-sm text-gray-700">
                                        {name}
                                    </span>
                                </div>
                            </ContextMenuTrigger>
                            <ContextMenuContent className="w-48">
                                <ContextMenuItem
                                    onClick={() => {
                                        setExpandedFolders((prev) => new Set([...prev, item.path]));
                                        setCreatingIn({ path: item.path, type: "file" });
                                    }}
                                >
                                    <File className="w-4 h-4 mr-2" />
                                    {t("appPreview.newFile") || "New File"}
                                </ContextMenuItem>
                            </ContextMenuContent>
                            {isExpanded && (
                                <div>
                                    {creatingIn && creatingIn.path === item.path && (
                                        <InlineFileInput
                                            type={creatingIn.type}
                                            level={level + 1}
                                            onConfirm={(name) => {
                                                if (creatingIn.type === "file" && onCreateFile) {
                                                    onCreateFile(name, item.path);
                                                } else if (creatingIn.type === "folder" && onCreateFolder) {
                                                    onCreateFolder(name, item.path);
                                                }
                                                setCreatingIn(null);
                                            }}
                                            onCancel={() => setCreatingIn(null)}
                                        />
                                    )}
                                    {renderFileTree(item.children, level + 1)}
                                </div>
                            )}
                        </ContextMenu>
                    );
                } else {
                    const isSelected = selectedFile?.id === item.component.id;
                    const isKeyboardSelected =
                        flatFileList[selectedIndex]?.id === item.component.id;
                    const isRenaming = renamingFileId === item.component.id;

                    return (
                        <ContextMenu key={item.component.id}>
                            <ContextMenuTrigger>
                                {isRenaming ? (
                                    <div
                                        className="flex items-center py-1 px-2"
                                        style={{
                                            paddingLeft: `${level * 12 + 8}px`,
                                        }}
                                    >
                                        <FileCode className="w-4 h-4 text-gray-500 mr-2 flex-shrink-0" />
                                        <Input
                                            ref={renameInputRef}
                                            value={renameValue}
                                            onChange={(e) =>
                                                setRenameValue(e.target.value)
                                            }
                                            onKeyDown={(e) =>
                                                handleRenameKeyDown(
                                                    e,
                                                    item.component
                                                )
                                            }
                                            onBlur={() => {
                                                const filePath =
                                                    item.component.filePath ||
                                                    item.component.name;
                                                const originalFileName = filePath.split("/").pop();
                                                handleRenameConfirm(
                                                    item.component,
                                                    renameValue,
                                                    originalFileName
                                                );
                                            }}
                                            className="h-6 text-sm px-2 py-0"
                                            style={{ minWidth: "100px" }}
                                        />
                                    </div>
                                ) : (
                                    <div
                                        className={`flex items-center py-1 px-2 hover:bg-gray-100 cursor-pointer ${
                                            isKeyboardSelected &&
                                            searchQuery.trim()
                                                ? "bg-blue-200 text-blue-900 font-medium"
                                                : isSelected
                                                ? "bg-blue-50 text-blue-700"
                                                : ""
                                        }`}
                                        style={{
                                            paddingLeft: `${level * 12 + 8}px`,
                                        }}
                                        onClick={() =>
                                            handleFileClick(item.component)
                                        }
                                    >
                                        <FileCode className="w-4 h-4 text-gray-500 mr-2" />
                                        <span className="text-sm text-gray-700">
                                            {name}
                                        </span>
                                    </div>
                                )}
                            </ContextMenuTrigger>
                            {!isRenaming && (
                                <ContextMenuContent className="w-48">
                                    <ContextMenuItem
                                        onClick={() =>
                                            handleRenameClick(item.component)
                                        }
                                    >
                                        <Pencil className="w-4 h-4 mr-2" />
                                        {t("appPreview.rename") || "Rename"}
                                    </ContextMenuItem>
                                    <ContextMenuItem
                                        onClick={() =>
                                            onDeleteFile &&
                                            onDeleteFile(
                                                item.component.filePath ||
                                                    item.component.name,
                                                item.component.id
                                            )
                                        }
                                        className="text-red-600 focus:text-red-600"
                                    >
                                        <X className="w-4 h-4 mr-2" />
                                        {t("appPreview.delete") || "Delete"}
                                    </ContextMenuItem>
                                </ContextMenuContent>
                            )}
                        </ContextMenu>
                    );
                }
            });
        };

        // Group search results by folder
        const groupResultsByFolder = (results) => {
            const grouped = {};

            results.forEach((result) => {
                const filePath =
                    result.component.filePath || result.component.name;
                const lastSlash = filePath.lastIndexOf("/");
                const folderPath =
                    lastSlash > 0 ? filePath.substring(0, lastSlash) : "(root)";

                if (!grouped[folderPath]) {
                    grouped[folderPath] = [];
                }
                grouped[folderPath].push(result);
            });

            // Sort folders alphabetically
            return Object.entries(grouped).sort(([a], [b]) =>
                a.localeCompare(b)
            );
        };

        const [expandedSearchFolders, setExpandedSearchFolders] = useState(
            new Set()
        );

        // Auto-expand all folders when search results change
        useEffect(() => {
            if (searchResults.length > 0) {
                const folders = new Set();
                searchResults.forEach((result) => {
                    const filePath =
                        result.component.filePath || result.component.name;
                    const lastSlash = filePath.lastIndexOf("/");
                    const folderPath =
                        lastSlash > 0
                            ? filePath.substring(0, lastSlash)
                            : "(root)";
                    folders.add(folderPath);
                });
                setExpandedSearchFolders(folders);
            }
        }, [searchResults]);

        const toggleSearchFolder = (folderPath) => {
            setExpandedSearchFolders((prev) => {
                const newSet = new Set(prev);
                if (newSet.has(folderPath)) {
                    newSet.delete(folderPath);
                } else {
                    newSet.add(folderPath);
                }
                return newSet;
            });
        };

        const highlightMatch = (text, query) => {
            if (!query.trim()) return text;

            const parts = [];
            let remaining = text;
            let lowerRemaining = remaining.toLowerCase();
            const lowerQuery = query.toLowerCase();
            let lastIndex = 0;

            while (lowerRemaining.includes(lowerQuery)) {
                const index = lowerRemaining.indexOf(lowerQuery);
                if (index > 0) {
                    parts.push(
                        <span key={lastIndex}>
                            {remaining.substring(0, index)}
                        </span>
                    );
                }
                parts.push(
                    <span
                        key={lastIndex + index}
                        className="bg-yellow-200 text-yellow-900 font-medium"
                    >
                        {remaining.substring(index, index + query.length)}
                    </span>
                );
                remaining = remaining.substring(index + query.length);
                lowerRemaining = remaining.toLowerCase();
                lastIndex += index + query.length;
            }

            if (remaining) {
                parts.push(<span key={lastIndex}>{remaining}</span>);
            }

            return parts.length > 0 ? parts : text;
        };

        const renderSearchResults = () => {
            if (!searchQuery.trim()) {
                return (
                    <div className="p-4 text-center text-gray-500">
                        <Search className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm">Search for text in your files</p>
                    </div>
                );
            }

            if (searchResults.length === 0) {
                return (
                    <div className="p-4 text-center text-gray-500">
                        <Search className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm">
                            No results found for "{searchQuery}"
                        </p>
                    </div>
                );
            }

            const groupedResults = groupResultsByFolder(searchResults);
            const totalMatches = searchResults.reduce(
                (acc, result) => acc + result.totalMatches,
                0
            );

            return (
                <div className="p-2">
                    <div className="text-xs text-gray-500 mb-2 px-2">
                        {totalMatches} results in {searchResults.length} files
                    </div>
                    {groupedResults.map(([folderPath, folderResults]) => {
                        const isExpanded =
                            expandedSearchFolders.has(folderPath);
                        const folderMatchCount = folderResults.reduce(
                            (acc, r) => acc + r.totalMatches,
                            0
                        );

                        return (
                            <div key={folderPath} className="mb-1">
                                {/* Folder Header */}
                                <div
                                    className="flex items-center py-1.5 px-2 hover:bg-gray-100 cursor-pointer sticky top-0 bg-gray-50 z-10"
                                    onClick={() =>
                                        toggleSearchFolder(folderPath)
                                    }
                                >
                                    {isExpanded ? (
                                        <ChevronDown className="w-3.5 h-3.5 text-gray-500 mr-1 flex-shrink-0" />
                                    ) : (
                                        <ChevronRight className="w-3.5 h-3.5 text-gray-500 mr-1 flex-shrink-0" />
                                    )}
                                    <Folder className="w-3.5 h-3.5 text-blue-500 mr-1.5 flex-shrink-0" />
                                    <span className="text-xs font-medium text-gray-600 truncate flex-1">
                                        {folderPath}
                                    </span>
                                    <span className="text-xs text-gray-400 ml-2">
                                        {folderMatchCount}
                                    </span>
                                </div>

                                {/* Files in folder */}
                                {isExpanded && (
                                    <div className="ml-3">
                                        {folderResults.map((result) => {
                                            const fileName = (
                                                result.component.filePath ||
                                                result.component.name
                                            )
                                                .split("/")
                                                .pop();

                                            return (
                                                <div
                                                    key={result.component.id}
                                                    className="mb-1"
                                                >
                                                    {/* File Header */}
                                                    <div
                                                        className="flex items-center py-1 px-2 hover:bg-gray-100 cursor-pointer"
                                                        onClick={() =>
                                                            handleFileClick(
                                                                result.component
                                                            )
                                                        }
                                                    >
                                                        <FileCode className="w-3.5 h-3.5 text-gray-500 mr-1.5 flex-shrink-0" />
                                                        <span className="text-xs font-medium text-gray-700 truncate flex-1">
                                                            {fileName}
                                                        </span>
                                                        <span className="text-xs text-gray-400 ml-2">
                                                            {
                                                                result.totalMatches
                                                            }
                                                        </span>
                                                    </div>

                                                    {/* Match lines */}
                                                    <div className="ml-5 border-l-2 border-gray-200">
                                                        {(
                                                            result.matches || []
                                                        )
                                                            .slice(0, 5)
                                                            .map(
                                                                (
                                                                    match,
                                                                    index
                                                                ) => (
                                                                    <div
                                                                        key={
                                                                            index
                                                                        }
                                                                        className="py-0.5 px-2 hover:bg-blue-50 cursor-pointer text-xs flex items-start group"
                                                                        onClick={() =>
                                                                            handleSearchResultClick(
                                                                                result.component,
                                                                                match.lineNumber
                                                                            )
                                                                        }
                                                                    >
                                                                        <span className="text-gray-400 mr-2 w-8 text-right flex-shrink-0 font-mono">
                                                                            {
                                                                                match.lineNumber
                                                                            }
                                                                        </span>
                                                                        <span className="text-gray-600 truncate font-mono group-hover:text-gray-900">
                                                                            {highlightMatch(
                                                                                match
                                                                                    .line
                                                                                    .length >
                                                                                    60
                                                                                    ? match.line.substring(
                                                                                          0,
                                                                                          60
                                                                                      ) +
                                                                                          "..."
                                                                                    : match.line,
                                                                                searchQuery
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                )
                                                            )}
                                                        {(result.matches
                                                            ?.length || 0) >
                                                            5 && (
                                                            <div className="py-0.5 px-2 text-xs text-gray-400 italic">
                                                                +
                                                                {result.matches
                                                                    .length -
                                                                    5}{" "}
                                                                more
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            );
        };

        return (
            <div className="w-full md:w-64 border-r bg-gray-50 flex flex-col h-full">
                {/* Tabs */}
                <div className="flex border-b">
                    {dashboardSection === "editor" && onBackToMenu && (
                        <button
                            className="px-2 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 border-r"
                            onClick={onBackToMenu}
                            title="Back to Menu"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                    )}
                    <button
                        className={`flex-1 px-4 py-2 text-sm font-medium ${
                            activeTab === "files"
                                ? "text-blue-600 border-b-2 border-blue-600 bg-white"
                                : "text-gray-500 hover:text-gray-700"
                        }`}
                        onClick={() => handleTabChange("files")}
                    >
                        Files
                    </button>
                    <button
                        className={`flex-1 px-4 py-2 text-sm font-medium ${
                            activeTab === "search"
                                ? "text-blue-600 border-b-2 border-blue-600 bg-white"
                                : "text-gray-500 hover:text-gray-700"
                        }`}
                        onClick={() => handleTabChange("search")}
                    >
                        Search
                    </button>
                    {onCloseSidebar && (
                        <button
                            className="px-2 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 border-l"
                            onClick={onCloseSidebar}
                            title="Close Sidebar"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>

                {/* Search Input */}
                <div className="p-3 border-b">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                            ref={searchInputRef}
                            placeholder={
                                activeTab === "files"
                                    ? "Search files..."
                                    : "Search in code..."
                            }
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            className="pl-10"
                        />
                    </div>
                </div>

                {/* Content */}
                <ScrollArea className="flex-1">
                    {activeTab === "files" ? (
                        <ContextMenu>
                            <ContextMenuTrigger asChild>
                                <div className="p-2 pb-20">
                                    {creatingIn && creatingIn.path === null && (
                                        <InlineFileInput
                                            type={creatingIn.type}
                                            level={0}
                                            onConfirm={(name) => {
                                                if (creatingIn.type === "file" && onCreateFile) {
                                                    onCreateFile(name, null);
                                                } else if (creatingIn.type === "folder" && onCreateFolder) {
                                                    onCreateFolder(name, null);
                                                }
                                                setCreatingIn(null);
                                            }}
                                            onCancel={() => setCreatingIn(null)}
                                        />
                                    )}
                                    {Object.keys(filteredFileTree).length ===
                                    0 && !creatingIn ? (
                                        <div className="p-4 text-center text-gray-500">
                                            <File className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                                            <p className="text-sm">
                                                {searchQuery.trim()
                                                    ? "No files match your search"
                                                    : "No files found"}
                                            </p>
                                        </div>
                                    ) : (
                                        renderFileTree(filteredFileTree)
                                    )}
                                </div>
                            </ContextMenuTrigger>
                            <ContextMenuContent className="w-48">
                                <ContextMenuItem
                                    onClick={() => setCreatingIn({ path: null, type: "file" })}
                                >
                                    <File className="w-4 h-4 mr-2" />
                                    {t("appPreview.newFile") || "New File"}
                                </ContextMenuItem>
                            </ContextMenuContent>
                        </ContextMenu>
                    ) : (
                        renderSearchResults()
                    )}
                </ScrollArea>
            </div>
        );
    }
);

CodeSection.displayName = "CodeSection";

export default CodeSection;
